import type {
  IGetEventsAggregatedRequestParams,
  IGetEventsAggregatedResponseBody,
} from "~/feature/dashboard/types";

export interface PageOffsetHistory {
  signature: string;
  offsetByPage: Record<number, number>;
}

const PAGINATION_COORDINATE_KEYS: ReadonlySet<string> = new Set([
  "page",
  "offset",
]);

export const pageOffsetSignature = (
  params: IGetEventsAggregatedRequestParams
): string =>
  JSON.stringify(
    Object.entries(params)
      .filter(
        ([key, value]) =>
          !PAGINATION_COORDINATE_KEYS.has(key) && value !== undefined
      )
      .sort(([a], [b]) => a.localeCompare(b))
  );

export const offsetForPage = (
  history: PageOffsetHistory | null,
  signature: string,
  page: number | undefined
): number | undefined =>
  history && history.signature === signature && page !== undefined
    ? history.offsetByPage[page]
    : undefined;

export const recordNextOffset = (
  history: PageOffsetHistory | null,
  signature: string,
  page: number | undefined,
  nextOffset: number | null | undefined
): PageOffsetHistory | null => {
  if (page === undefined || typeof nextOffset !== "number") {
    return history;
  }

  const isSameScope = history?.signature === signature;

  if (isSameScope && history.offsetByPage[page + 1] === nextOffset) {
    return history;
  }

  return {
    signature,
    offsetByPage: {
      ...(isSameScope ? history.offsetByPage : undefined),
      [page + 1]: nextOffset,
    },
  };
};

type PageOffsetResponse = Pick<
  IGetEventsAggregatedResponseBody,
  "page" | "nextOffset"
>;

export const createPageOffsetStore = () => {
  let history: PageOffsetHistory | null = null;

  return {
    withTrackedOffset: (
      params: IGetEventsAggregatedRequestParams
    ): IGetEventsAggregatedRequestParams => {
      const offset = offsetForPage(
        history,
        pageOffsetSignature(params),
        params.page
      );

      return offset === undefined ? params : { ...params, offset };
    },
    recordResponse: (
      params: IGetEventsAggregatedRequestParams,
      response: PageOffsetResponse | null | undefined
    ): void => {
      if (!response) {
        return;
      }

      history = recordNextOffset(
        history,
        pageOffsetSignature(params),
        response.page,
        response.nextOffset
      );
    },
  };
};

export const aggregatedPageOffsets = createPageOffsetStore();
